// src/components/ScrollTabs.js —— 可横向滑动的 Tabs；左右箭头仅在标题溢出时显示
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tabs } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

const STEP = 180;

const ScrollTabs = ({ className, tabBarExtraContent, items, activeKey, ...rest }) => {
  const wrapRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const measure = useCallback(() => {
    const nav = wrapRef.current && wrapRef.current.querySelector('.ant-tabs-nav-wrap');
    if (!nav) {
      setOverflow(false);
      setCanLeft(false);
      setCanRight(false);
      return;
    }
    const overflowing = nav.scrollWidth > nav.clientWidth + 1;
    setOverflow(overflowing);
    setCanLeft(nav.scrollLeft > 1);
    setCanRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    measure();
    const root = wrapRef.current;
    const nav = root && root.querySelector('.ant-tabs-nav-wrap');
    const list = nav && nav.querySelector('.ant-tabs-nav-list');
    if (!nav) return undefined;

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (ro) {
      ro.observe(nav);
      if (list) ro.observe(list);
      if (root) ro.observe(root);
    }
    nav.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      nav.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, items, activeKey]);

  useEffect(() => {
    const id = window.requestAnimationFrame(measure);
    const t = window.setTimeout(measure, 80);
    return () => {
      window.cancelAnimationFrame(id);
      window.clearTimeout(t);
    };
  }, [measure, items, activeKey]);

  const slide = (dir) => {
    const nav = wrapRef.current && wrapRef.current.querySelector('.ant-tabs-nav-wrap');
    if (nav) nav.scrollBy({ left: dir * STEP, behavior: 'smooth' });
  };

  const arrows = overflow ? (
    <span className="scroll-tabs-arrows">
      <button
        type="button"
        className="scroll-tabs-arrow"
        aria-label="向左滑动"
        disabled={!canLeft}
        onClick={() => slide(-1)}
      >
        <LeftOutlined />
      </button>
      <button
        type="button"
        className="scroll-tabs-arrow"
        aria-label="向右滑动"
        disabled={!canRight}
        onClick={() => slide(1)}
      >
        <RightOutlined />
      </button>
    </span>
  ) : null;

  let extra = tabBarExtraContent;
  if (arrows) {
    const isSplit = extra && typeof extra === 'object' && ('left' in extra || 'right' in extra);
    if (isSplit) {
      extra = {
        left: extra.left,
        right: (
          <span className="scroll-tabs-extra-right">
            {extra.right}
            {arrows}
          </span>
        ),
      };
    } else if (extra) {
      extra = {
        right: (
          <span className="scroll-tabs-extra-right">
            {extra}
            {arrows}
          </span>
        ),
      };
    } else {
      extra = { right: arrows };
    }
  }

  return (
    <div className={`scroll-tabs${className ? ` ${className}` : ''}`} ref={wrapRef}>
      <Tabs {...rest} items={items} activeKey={activeKey} tabBarExtraContent={extra} />
    </div>
  );
};

export default ScrollTabs;
